import React, { useState, useEffect } from 'react';
import { X, Save, Bot, Package, DollarSign, Calendar, Plus, Trash2, Power } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_URL } from '../../config';

export default function ManyChatBotConfig({ page, onClose }) {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('base');
  const [formData, setFormData] = useState({
    company_context: '',
    response_instructions: '',
    transfer_keywords: '',
    transfer_message: 'Votre demande a été transférée à notre équipe.',
    faq: [],
    products: [],
    sale_type: 'payment_confirmed',
    sale_conditions: {
      require_payment: true,
      require_product: false,
      require_address: false,
      require_phone: false,
      payment_keywords: 'j\'ai payé, efa nandefa vola, payment done, paid',
      payment_intent_keywords: 'je paie maintenant, handefa vola izao, paying now'
    },
    rdv_enabled: false,
    rdv_conditions: {
      require_date: true,
      require_time: true,
      require_service: false,
      rdv_keywords: 'rendez-vous, rdv, appointment, fotoana, fihaonana'
    }
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/messenger-configs`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const pageConfig = data.configs.find(c => c.client_id === page.$id);
        
        if (pageConfig) {
          setConfig(pageConfig);
          
          // Parser avec gestion d'erreur
          let products = [];
          let saleConditions = formData.sale_conditions;
          let rdvConditions = formData.rdv_conditions;
          
          try {
            if (pageConfig.products) products = JSON.parse(pageConfig.products);
          } catch (e) { console.error('Parse products:', e); }
          
          try {
            if (pageConfig.sale_conditions) {
              saleConditions = { ...saleConditions, ...JSON.parse(pageConfig.sale_conditions) };
            }
          } catch (e) { console.error('Parse sale_conditions:', e); }
          
          try {
            if (pageConfig.rdv_conditions) {
              rdvConditions = { ...rdvConditions, ...JSON.parse(pageConfig.rdv_conditions) };
            }
          } catch (e) { console.error('Parse rdv_conditions:', e); }
          
          let faq = [];
          try {
            faq = JSON.parse(pageConfig.faq || '[]');
          } catch (e) { console.error('Parse faq:', e); }
          
          setFormData({
            company_context: pageConfig.company_context || '',
            response_instructions: pageConfig.response_instructions || '',
            transfer_keywords: pageConfig.transfer_keywords || '',
            transfer_message: pageConfig.transfer_message || 'Votre demande a été transférée à notre équipe.',
            faq,
            products,
            sale_type: pageConfig.sale_type || 'payment_confirmed',
            sale_conditions: saleConditions,
            rdv_enabled: pageConfig.rdv_enabled || false,
            rdv_conditions: rdvConditions
          });
        }
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
    setLoading(false);
  };

  const saveConfig = async () => {
    if (!formData.company_context || !formData.response_instructions) {
      toast.error('Contexte et instructions requis');
      return;
    }

    toast.loading('Enregistrement...', { id: 'save' });
    
    try {
      const url = config? `${API_URL}/api/admin/messenger-configs/${config.$id}`
      : `${API_URL}/api/admin/messenger-configs`;
    
    const method = config ? 'PUT' : 'POST';
    
    const payload = config 
      ? formData 
      : { ...formData, client_id: page.$id };
    
    const response = await fetch(url, {
      method,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    const data = await response.json();
    
    if (response.ok && data.success) {
      toast.success('Configuration sauvegardée !', { id: 'save' });
      onClose();
    } else {
      toast.error(data.message || 'Erreur', { id: 'save' });
    }
  } catch (error) {
    toast.error('Erreur réseau', { id: 'save' });
  }
};

const toggleBot = async () => {
  if (!config) {
    toast.error('Créez d\'abord une configuration');
    return;
  }

  toast.loading('Changement statut...', { id: 'toggle' });
  
  try {
    const response = await fetch(`${API_URL}/api/clients/${page.$id}/messenger-bot/toggle`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      }
    });
    
    const data = await response.json();
    
    if (response.ok) {
      toast.success(data.message, { id: 'toggle' });
      setConfig({ ...config, is_active: data.is_active });
    } else {
      toast.error(data.message || 'Erreur', { id: 'toggle' });
    }
  } catch (error) {
    toast.error('Erreur réseau', { id: 'toggle' });
  }
};

// Fonctions FAQ
const addFaqItem = () => {
  setFormData({
    ...formData,
    faq: [...formData.faq, { question: '', answer: '' }]
  });
};

const removeFaqItem = (index) => {
  setFormData({
    ...formData,
    faq: formData.faq.filter((_, i) => i !== index)
  });
};

const updateFaqItem = (index, field, value) => {
  const newFaq = [...formData.faq];
  newFaq[index][field] = value;
  setFormData({ ...formData, faq: newFaq });
};

// Fonctions Produits
const addProduct = () => {
  setFormData({
    ...formData,
    products: [...formData.products, { 
      name: '', 
      description: '', 
      price: '', 
      currency: 'Ar',
      stock: 'available'
    }]
  });
};

const removeProduct = (index) => {
  setFormData({
    ...formData,
    products: formData.products.filter((_, i) => i !== index)
  });
};

const updateProduct = (index, field, value) => {
  const newProducts = [...formData.products];
  newProducts[index][field] = value;
  setFormData({ ...formData, products: newProducts });
};

return (
  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-slate-900 border border-white/10 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-slate-900 border-b border-white/10 p-6 flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-white mb-1">Configuration Bot</h3>
          <p className="text-blue-200">{page.facebook_page_name}</p>
        </div>
        
        <div className="flex items-center gap-3">
          {config && (
            <button
              onClick={toggleBot}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                config.is_active
                  ? 'bg-green-500/20 text-green-300 hover:bg-green-500/30'
                  : 'bg-gray-500/20 text-gray-300 hover:bg-gray-500/30'
              }`}
            >
              <Power className="w-4 h-4" />
              {config.is_active ? 'Bot Actif' : 'Bot Inactif'}
            </button>
          )}
          
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-all">
            <X className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/10 px-6 flex gap-2 overflow-x-auto">
        {[
          { id: 'base', label: 'Configuration', icon: Bot },
          { id: 'products', label: 'Produits', icon: Package },
          { id: 'sales', label: 'Ventes', icon: DollarSign },
          { id: 'rdv', label: 'RDV', icon: Calendar }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-green-500 text-green-400'
                : 'border-transparent text-white/60 hover:text-white'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="text-center py-12">
            <Bot className="w-12 h-12 text-green-400 animate-spin mx-auto mb-4" />
            <p className="text-white/60">Chargement...</p>
          </div>
        ) : (
          <>
            {/* TAB: Base */}
            {activeTab === 'base' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-white font-medium mb-2">Contexte Entreprise *</label>
                  <textarea
                    value={formData.company_context}
                    onChange={(e) => setFormData({ ...formData, company_context: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white placeholder-white/40 min-h-32"
                    placeholder="Ex: Restaurant Tana, cuisine malgache. Horaires: 11h-22h. Spécialités: romazava, ravitoto."
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Instructions de Réponse *</label>
                  <textarea
                    value={formData.response_instructions}
                    onChange={(e) => setFormData({ ...formData, response_instructions: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white placeholder-white/40 min-h-32"
                    placeholder="Ex: Chaleureux et accueillant. Français et malgasy. Max 2-3 phrases."
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Mots-clés Transfert Humain</label>
                  <input
                    type="text"
                    value={formData.transfer_keywords}
                    onChange={(e) => setFormData({ ...formData, transfer_keywords: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white"
                    placeholder="urgent, problème, réclamation"
                  />
                </div>

                {/* FAQ */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-white font-medium">FAQ</label>
                    <button
                      type="button"
                      onClick={addFaqItem}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-300"
                    >
                      <Plus className="w-4 h-4" />
                      Ajouter
                    </button>
                  </div>

                  <div className="space-y-3">
                    {formData.faq.map((item, idx) => (
                      <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-4">
                        <div className="flex justify-between mb-2">
                          <span className="text-sm text-white/60">FAQ #{idx + 1}</span>
                          <button onClick={() => removeFaqItem(idx)} className="text-red-400">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <input
                          type="text"
                          value={item.question}
                          onChange={(e) => updateFaqItem(idx, 'question', e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white mb-2"
                          placeholder="Question ou mot-clé"
                        />
                        <textarea
                          value={item.answer}
                          onChange={(e) => updateFaqItem(idx, 'answer', e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white"
                          placeholder="Réponse"
                          rows="2"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* TAB: Produits */}
            {activeTab === 'products' && (
              <div className="space-y-6">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
                  <Package className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-white font-medium mb-1">Catalogue Produits</h4>
                    <p className="text-sm text-blue-200">
                      Le bot pourra répondre aux questions sur les prix et disponibilité.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={addProduct}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-300"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter Produit
                  </button>
                </div>

                <div className="space-y-4">
                  {formData.products.map((product, idx) => (
                    <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-4">
                      <div className="flex justify-between mb-3">
                        <span className="text-white font-medium">Produit #{idx + 1}</span>
                        <button onClick={() => removeProduct(idx)} className="text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-white/60 text-sm mb-1">Nom *</label>
                          <input
                            type="text"
                            value={product.name}
                            onChange={(e) => updateProduct(idx, 'name', e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white"
                            placeholder="Romazava"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-white/60 text-sm mb-1">Prix *</label>
                            <input
                              type="text"
                              value={product.price}
                              onChange={(e) => updateProduct(idx, 'price', e.target.value)}
                              className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white"
                              placeholder="15000"
                            />
                          </div>
                          <div>
                            <label className="block text-white/60 text-sm mb-1">Devise</label>
                            <select
                              value={product.currency}
                              onChange={(e) => updateProduct(idx, 'currency', e.target.value)}
                              className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white"
                            >
                              <option value="Ar">Ar</option>
                              <option value="€">€</option>
                              <option value="$">$</option>
                            </select>
                          </div>
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-white/60 text-sm mb-1">Description</label>
                          <textarea
                            value={product.description}
                            onChange={(e) => updateProduct(idx, 'description', e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white"
                            placeholder="Plat traditionnel malgache"
                            rows="2"
                          />
                        </div>

                        <div>
                          <label className="block text-white/60 text-sm mb-1">Stock</label>
                          <select
                            value={product.stock}
                            onChange={(e) => updateProduct(idx, 'stock', e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white"
                          >
                            <option value="available">✅ Disponible</option>
                            <option value="limited">⚠️ Stock limité</option>
                            <option value="out_of_stock">❌ Rupture</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}

                  {formData.products.length === 0 && (
                    <div className="text-center py-8 text-white/40">
                      <Package className="w-12 h-12 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Aucun produit ajouté</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TAB: Ventes */}
            {activeTab === 'sales' && (
              <div className="space-y-6">
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-green-400 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-white font-medium mb-1">Détection de Vente</h4>
                    <p className="text-sm text-green-200">
                      Le bot alertera sur Telegram quand une vente est détectée.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-white font-medium mb-3">Type de vente validée</label>
                  <div className="space-y-3">
                    {[
                      { 
                        value: 'payment_confirmed', 
                        label: '💳 Paiement Confirmé',
                        desc: 'Client dit "j\'ai payé", "efa nandefa vola"'
                      },
                      { 
                        value: 'payment_intent', 
                        label: '⚡ Paiement OU Intention',
                        desc: 'Inclut "je paie maintenant", "handefa vola izao"'
                      },
                      { 
                        value: 'order_with_details', 
                        label: '📋 Commande Complète',
                        desc: 'Produit + adresse + téléphone'
                      }
                    ].map(option => (
                      <label
                        key={option.value}
                        className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          formData.sale_type === option.value
                            ? 'border-green-500 bg-green-500/10'
                            : 'border-white/10 bg-white/5 hover:border-white/20'
                        }`}
                      >
                        <input
                          type="radio"
                          name="sale_type"
                          value={option.value}
                          checked={formData.sale_type === option.value}
                          onChange={(e) => setFormData({ ...formData, sale_type: e.target.value })}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="text-white font-medium mb-1">{option.label}</div>
                          <div className="text-sm text-white/60">{option.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Conditions supplémentaires */}
                <div className="bg-white/5 rounded-xl p-4 space-y-3">
                  <h5 className="text-white font-medium mb-3">Conditions supplémentaires</h5>
                  
                  {[
                    { key: 'require_product', label: 'Produit mentionné', desc: 'Client doit mentionner un produit' },
                    { key: 'require_address', label: 'Adresse requise', desc: 'Client doit donner son adresse' },
                    { key: 'require_phone', label: 'Téléphone requis', desc: 'Client doit donner son numéro' }
                  ].map(condition => (
                    <label key={condition.key} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.sale_conditions[condition.key]}
                        onChange={(e) => setFormData({
                          ...formData,
                          sale_conditions: { ...formData.sale_conditions, [condition.key]: e.target.checked }
                        })}
                        className="w-5 h-5"
                      />
                      <div className="flex-1">
                        <div className="text-white">{condition.label}</div>
                        <div className="text-sm text-white/60">{condition.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Mots-clés Paiement Confirmé</label>
                  <input
                    type="text"
                    value={formData.sale_conditions.payment_keywords}
                    onChange={(e) => setFormData({
                      ...formData,
                      sale_conditions: { ...formData.sale_conditions, payment_keywords: e.target.value }
                    })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white"
                    placeholder="j'ai payé, efa nandefa vola"
                  />
                </div>
              </div>
            )}

            {/* TAB: RDV */}
            {activeTab === 'rdv' && (
              <div className="space-y-6">
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-purple-400 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-white font-medium mb-1">Prise de Rendez-vous</h4>
                    <p className="text-sm text-purple-200">
                      Le bot peut détecter et traiter les demandes de RDV.
                    </p>
                  </div>
                </div>

                <label className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.rdv_enabled}
                    onChange={(e) => setFormData({ ...formData, rdv_enabled: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <div className="flex-1">
                    <div className="text-white font-medium">Activer la prise de RDV</div>
                    <div className="text-sm text-white/60">Le bot détectera les demandes de rendez-vous</div>
                  </div>
                </label>

                {formData.rdv_enabled && (
                  <>
                    <div>
                      <label className="block text-white font-medium mb-2">Mots-clés RDV</label>
                      <input
                        type="text"
                        value={formData.rdv_conditions.rdv_keywords}
                        onChange={(e) => setFormData({
                          ...formData,
                          rdv_conditions: { ...formData.rdv_conditions, rdv_keywords: e.target.value }
                        })}
                        className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white"
                        placeholder="rendez-vous, rdv, fanendrena"
                      />
                    </div>

                    <div className="bg-white/5 rounded-xl p-4 space-y-3">
                      <h5 className="text-white font-medium mb-3">Informations requises</h5>
                      
                      {[
                        { key: 'require_date', label: 'Date requise', desc: 'Le client doit mentionner une date' },
                        { key: 'require_time', label: 'Heure requise', desc: 'Le client doit mentionner une heure' },
                        { key: 'require_service', label: 'Service requis', desc: 'Le client doit préciser le motif' }
                      ].map(condition => (
                        <label key={condition.key} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.rdv_conditions[condition.key]}
                            onChange={(e) => setFormData({
                              ...formData,
                              rdv_conditions: { ...formData.rdv_conditions, [condition.key]: e.target.checked }
                            })}
                            className="w-5 h-5"
                          />
                          <div className="flex-1">
                            <div className="text-white">{condition.label}</div>
                            <div className="text-sm text-white/60">{condition.desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="bg-slate-900 border-t border-white/10 p-6 flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium transition-all"
        >
          Annuler
        </button>
        <button
          onClick={saveConfig}
          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold transition-all flex items-center justify-center gap-2"
        >
          <Save className="w-5 h-5" />
          Enregistrer
        </button>
      </div>
    </div>
  </div>
);
}